class BundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.requiredTotal = Number(this.dataset.requiredTotal || 0);
    this.paidQuantity = Number(this.dataset.paidQuantity || this.requiredTotal);
    this.freeQuantity = Number(this.dataset.freeQuantity || 0);
    this.layoutMode = this.dataset.layoutMode || 'collections';
    this.kitName = this.dataset.kitName || 'Bundle';
    this.kitType = this.dataset.kitType || '';
    this.productId = this.dataset.productId || '';
    this.productHandle = this.dataset.productHandle || '';
    this.kitVariantId = this.dataset.kitVariantId || '';
    this.cartMode = this.dataset.cartMode || 'cart_transform';
    this.giftBoxVariantId = this.dataset.giftBoxVariantId || '';
    this.giftBoxTitle = this.dataset.giftBoxTitle || 'Gift box';
    this.picks = [];

    this.slotsEl = this.querySelector('[data-bundle-slots]');
    this.counterEl = this.querySelector('[data-bundle-counter]');
    this.progressEl = this.querySelector('[data-bundle-progress]');
    this.summaryEl = this.querySelector('[data-bundle-summary]');
    this.addButton = this.querySelector('[data-bundle-add]');
    this.resetButton = this.querySelector('[data-bundle-reset]');
    this.messageEl = this.querySelector('[data-bundle-message]');
    this.items = [...this.querySelectorAll('[data-bundle-item]')];
    this.zoomButtons = [...this.querySelectorAll('[data-bundle-zoom]')];
    this.groups = [...this.querySelectorAll('[data-bundle-group]')];
    this.handleZoomKeydown = (event) => {
      if (event.key === 'Escape') this.closeZoom();
    };

    this.items.forEach((item) => {
      item.addEventListener('click', () => this.addPick(item));
    });
    this.zoomButtons.forEach((button) => {
      button.addEventListener('click', () => this.openZoom(button));
    });

    this.addButton?.addEventListener('click', () => this.addToCart());
    this.resetButton?.addEventListener('click', () => this.reset());
    this.querySelectorAll('[data-bundle-tab]').forEach((tab) => {
      tab.addEventListener('click', () => this.activateTab(tab));
    });

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleZoomKeydown);
  }

  ensureZoomModal() {
    if (this.zoomModal) return;

    const modal = document.createElement('div');
    modal.className = 'bundle-builder__zoom-modal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Imagine produs');
    modal.innerHTML = `
      <div class="bundle-builder__zoom-panel">
        <button type="button" class="bundle-builder__zoom-close" aria-label="Inchide imaginea">X</button>
        <img class="bundle-builder__zoom-image" alt="">
        <p class="bundle-builder__zoom-title"></p>
      </div>
    `;

    modal.addEventListener('click', (event) => {
      if (event.target === modal) this.closeZoom();
    });

    modal.querySelector('.bundle-builder__zoom-close')?.addEventListener('click', () => this.closeZoom());
    this.appendChild(modal);
    this.zoomModal = modal;
  }

  openZoom(button) {
    const image = button.dataset.zoomImage;
    if (!image) return;

    this.ensureZoomModal();
    this.lastZoomTrigger = button;

    const img = this.zoomModal.querySelector('.bundle-builder__zoom-image');
    const title = this.zoomModal.querySelector('.bundle-builder__zoom-title');
    const close = this.zoomModal.querySelector('.bundle-builder__zoom-close');
    const zoomTitle = button.dataset.zoomTitle || 'Produs';

    img.src = image;
    img.alt = zoomTitle;
    title.textContent = zoomTitle;
    this.zoomModal.hidden = false;
    document.addEventListener('keydown', this.handleZoomKeydown);
    close?.focus({ preventScroll: true });
  }

  closeZoom() {
    if (!this.zoomModal || this.zoomModal.hidden) return;

    this.zoomModal.hidden = true;
    document.removeEventListener('keydown', this.handleZoomKeydown);
    this.lastZoomTrigger?.focus({ preventScroll: true });
  }

  activateTab(tab) {
    const target = tab.dataset.bundleTab;
    if (!target) return;

    this.querySelectorAll('[data-bundle-tab]').forEach((button) => {
      const isActive = button === tab;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;
    });

    this.querySelectorAll('[data-bundle-tab-panel]').forEach((panel) => {
      const isActive = panel.dataset.bundleTabPanel === target;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });
  }

  addPick(item) {
    const group = this.getGroup(item.dataset.groupId);
    const groupCount = this.countGroup(item.dataset.groupId);
    const stockLimit = this.stockLimit(item);
    const variantCount = this.countVariant(item.dataset.variantId);

    if (this.picks.length >= this.requiredTotal) {
      this.showMessage(`Ai ales deja ${this.requiredTotal} produse. Scoate un produs din sloturile de sus ca sa alegi altul.`);
      return;
    }

    if (Number.isFinite(stockLimit) && stockLimit <= 0) {
      this.showMessage(`Produsul ${item.dataset.title || 'selectat'} nu mai este in stoc.`);
      return;
    }

    if (Number.isFinite(stockLimit) && variantCount >= stockLimit) {
      const stockText = stockLimit === 1 ? 'o bucata' : `${stockLimit} bucati`;
      this.showMessage(`Sunt disponibile doar ${stockText} din ${item.dataset.title || 'acest produs'}.`);
      return;
    }

    if (group && Number(group.dataset.max || 0) > 0 && groupCount >= Number(group.dataset.max)) {
      this.showMessage(`Ai atins maximul pentru ${group.dataset.title || 'aceasta sectiune'}.`);
      return;
    }

    this.picks.push(this.itemData(item));
    this.hideMessage();
    this.clearValidationPrompt();
    this.render();
  }

  itemData(item) {
    return {
      variantId: item.dataset.variantId,
      productId: item.dataset.productId,
      groupId: item.dataset.groupId,
      title: item.dataset.title,
      image: item.dataset.image
    };
  }

  getGroup(groupId) {
    return this.groups.find((group) => group.dataset.groupId === groupId);
  }

  countGroup(groupId) {
    return this.picks.filter((pick) => pick.groupId === groupId).length;
  }

  countVariant(variantId) {
    return this.picks.filter((pick) => pick.variantId === variantId).length;
  }

  stockLimit(item) {
    const inventoryManagement = item.dataset.inventoryManagement || '';
    const inventoryPolicy = item.dataset.inventoryPolicy || '';

    if (!inventoryManagement || inventoryPolicy === 'continue') {
      return Infinity;
    }

    return Math.max(Number(item.dataset.inventoryQuantity || 0), 0);
  }

  removePick(index) {
    this.picks.splice(index, 1);
    this.hideMessage();
    this.clearValidationPrompt();
    this.render();
  }

  reset() {
    this.picks = [];
    this.hideMessage();
    this.clearValidationPrompt();
    this.render();
  }

  isComplete() {
    if (this.picks.length !== this.requiredTotal) return false;

    return this.groups.every((group) => {
      const count = this.countGroup(group.dataset.groupId);
      const min = Number(group.dataset.min || 0);
      const max = Number(group.dataset.max || this.requiredTotal);
      return count >= min && count <= max;
    });
  }

  validationText() {
    if (this.picks.length < this.requiredTotal) {
      const remaining = this.requiredTotal - this.picks.length;
      return `Mai trebuie selectate ${remaining} ${remaining === 1 ? 'produs' : 'produse'}.`;
    }

    const invalidGroup = this.groups.find((group) => {
      const count = this.countGroup(group.dataset.groupId);
      return count < Number(group.dataset.min || 0);
    });

    if (invalidGroup) {
      const min = Number(invalidGroup.dataset.min || 0);
      const count = this.countGroup(invalidGroup.dataset.groupId);
      return `${invalidGroup.dataset.title || 'Sectiunea'}: mai trebuie ${min - count} produse.`;
    }

    return this.freeQuantity > 0
        ? `Pachet complet: ${this.paidQuantity} platite + ${this.freeQuantity} GRATIS.`
        : 'Pachet complet.';
  }

  validationState() {
    return this.isComplete() ? 'complete' : 'incomplete';
  }

  scrollElementAboveSticky(element) {
    if (!element) return;

    window.setTimeout(() => {
      const rect = element.getBoundingClientRect();
      const stickyReserve = Math.min(window.innerHeight * 0.28, 220);
      const targetTop = window.scrollY + rect.top - ((window.innerHeight - stickyReserve) / 2);

      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: 'smooth'
      });
    }, 0);
  }

  showValidationPrompt() {
    if (!this.summaryEl) return;

    this.hideMessage();
    this.renderFooter();
    this.summaryEl.classList.add('is-visible', 'is-attention');
    this.summaryEl.focus({ preventScroll: true });
    this.scrollElementAboveSticky(this.summaryEl);
  }

  clearValidationPrompt() {
    this.summaryEl?.classList.remove('is-visible', 'is-attention');
  }

  renderSlots() {
    if (!this.slotsEl) return;
    this.slotsEl.innerHTML = '';

    for (let index = 0; index < this.requiredTotal; index += 1) {
      const slot = document.createElement('button');
      slot.type = 'button';
      slot.className = 'bundle-builder__slot';
      slot.setAttribute('aria-label', `Slot ${index + 1}`);

      if (this.picks[index]) {
        const pick = this.picks[index];
        slot.classList.add('is-filled');
        slot.title = `${pick.title} - elimina`;
        slot.setAttribute('aria-label', `Elimina ${pick.title}`);

        if (pick.image) {
          const image = document.createElement('img');
          image.src = pick.image;
          image.alt = '';
          slot.appendChild(image);
        } else {
          slot.textContent = index + 1;
        }

        const remove = document.createElement('span');
        remove.className = 'bundle-builder__slot-remove';
        remove.textContent = 'X';
        remove.setAttribute('aria-hidden', 'true');
        slot.appendChild(remove);

        slot.addEventListener('click', () => this.removePick(index));
      } else {
        slot.textContent = index + 1;
      }

      if (this.freeQuantity > 0 && index >= this.paidQuantity) {
        const free = document.createElement('span');
        free.className = 'bundle-builder__free';
        free.textContent = 'GRATIS';
        slot.appendChild(free);
      }

      this.slotsEl.appendChild(slot);
    }
  }

  renderItems() {
    this.items.forEach((item) => {
      const qty = this.picks.filter((pick) => {
        return pick.variantId === item.dataset.variantId && pick.groupId === item.dataset.groupId;
      }).length;
      const variantQty = this.countVariant(item.dataset.variantId);
      const stockLimit = this.stockLimit(item);
      const atStockLimit = Number.isFinite(stockLimit) && variantQty >= stockLimit;

      item.classList.toggle('is-selected', qty > 0);
      item.classList.toggle('has-multiple', qty > 1);
      item.classList.toggle('is-at-stock-limit', atStockLimit);
      item.setAttribute('aria-disabled', atStockLimit ? 'true' : 'false');

      const qtyEl = item.querySelector('[data-item-qty]');
      if (qtyEl) qtyEl.textContent = qty > 0 ? qty : '';
    });
  }

  renderGroups() {
    this.groups.forEach((group) => {
      const countEl = group.querySelector('[data-group-count]');
      if (!countEl) return;

      const max = Number(group.dataset.max || this.requiredTotal);
      countEl.textContent = `${this.countGroup(group.dataset.groupId)}/${max}`;
    });
  }

  renderFooter() {
    const complete = this.isComplete();
    const progress = this.requiredTotal > 0
        ? Math.min(this.picks.length / this.requiredTotal, 1) * 100
        : 0;

    if (this.counterEl) this.counterEl.textContent = `${this.picks.length} / ${this.requiredTotal}`;
    if (this.progressEl) this.progressEl.style.width = `${progress}%`;
    if (this.summaryEl) {
      this.summaryEl.textContent = this.validationText();
      this.summaryEl.dataset.state = this.validationState();
    }
    if (this.addButton) this.addButton.disabled = !complete;
  }

  render() {
    this.renderSlots();
    this.renderItems();
    this.renderGroups();
    this.renderFooter();
  }

  showMessage(message, success = false) {
    if (!this.messageEl) return;
    this.messageEl.textContent = message;
    this.messageEl.classList.add('is-visible');
    this.messageEl.dataset.status = success ? 'success' : 'error';

    if (!success) {
      this.messageEl.focus({ preventScroll: true });
      this.scrollElementAboveSticky(this.messageEl);
    }
  }

  hideMessage() {
    if (!this.messageEl) return;
    this.messageEl.classList.remove('is-visible');
    this.messageEl.textContent = '';
    this.messageEl.removeAttribute('data-status');
  }

  usesCartTransform() {
    return this.cartMode === 'cart_transform' && Boolean(this.kitVariantId);
  }

  async nextSetNumber() {
    const cart = await fetch('/cart.js').then((response) => response.json());
    let maxSet = 0;

    (cart.items || []).forEach((item) => {
      const props = item.properties || {};

      let setNumber = null;

      if (props._set) {
        setNumber = parseInt(props._set, 10);
      }

      if (!setNumber && props._bundle_data) {
        try {
          const bundleData = JSON.parse(props._bundle_data);
          if (bundleData?.set) {
            setNumber = parseInt(bundleData.set, 10);
          }
        } catch (error) {
          // ignore invalid JSON
        }
      }

      if (!Number.isNaN(setNumber) && setNumber > maxSet) {
        maxSet = setNumber;
      }
    });

    return maxSet + 1;
  }

  buildCartTransformItemProperties(pick, index, kitId, setNum, roleOverride = null) {
    const role = roleOverride || (this.freeQuantity > 0 && index >= this.paidQuantity ? 'free' : 'paid');
    const requiredTotal = this.requiredTotal + (this.giftBoxVariantId ? 1 : 0);

    return {
      Kit: this.kitName,

      _bundle_id: kitId,
      _bundle_parent_variant_gid: `gid://shopify/ProductVariant/${this.kitVariantId}`,

      // _bundle_data: JSON.stringify({
      //   title: this.kitName,
      //   type: this.kitType,
      //   targetPriceCents: Number(this.dataset.price || 0),
      //   requiredTotal,
      //   requiredTotal: this.requiredTotal,
      //   paidQuantity: this.paidQuantity,
      //   freeQuantity: this.freeQuantity,
      //   index: index + 1,
      //   role,
      //   section: pick.groupId,
      //   set: setNum,
      //   gift: role === 'gift'
      // }),

      _bundle_title: this.kitName,
      _bundle_index: String(index + 1),
      _bundle_role: role,
      _set: String(setNum),

      _bundle_target_price_cents: String(Number(this.dataset.price || 0)),
      _bundle_required_total: String(this.requiredTotal),
    };
  }

  buildCartTransformItems(kitId, setNum) {
    const items = this.picks.map((pick, index) => ({
      id: Number(pick.variantId),
      quantity: 1,
      properties: this.buildCartTransformItemProperties(pick, index, kitId, setNum)
    }));

    if (this.giftBoxVariantId) {
      const giftIndex = items.length;
      const giftPick = {
        variantId: this.giftBoxVariantId,
        productId: '',
        groupId: 'gift-box',
        title: this.giftBoxTitle,
        image: ''
      };

      items.push({
        id: Number(this.giftBoxVariantId),
        quantity: 1,
        properties: this.buildCartTransformItemProperties(giftPick, giftIndex, kitId, setNum, 'gift')
      });
    }

    return items;
  }

  async addToCart() {
    if (!this.isComplete() || !this.addButton) {
      this.showValidationPrompt();
      return;
    }

    if (!this.usesCartTransform()) {
      this.showMessage('Acest kit trebuie configurat cu modul Cart transform.');
      return;
    }

    this.addButton.disabled = true;
    const originalText = this.addButton.textContent;
    this.addButton.textContent = 'Se adauga...';

    try {
      const kitId = `bundle-${this.productId}-${Date.now()}`;
      const setNum = await this.nextSetNumber();
      const items = this.buildCartTransformItems(kitId, setNum);

      if (items.some((item) => !item.id)) {
        throw new Error('Nu am putut identifica toate produsele din kit.');
      }

      console.log('BUNDLE ADD BODY', JSON.stringify({
  items,
  sections: 'cart-drawer',
  sections_url: window.location.pathname
}, null, 2));

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Nu am putut adauga pachetul in cos.');
      }

      await response.json();
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      this.reset();
      this.showMessage('Pachetul a fost adaugat in cos.', true);
    } catch (error) {
      this.showMessage(error.message || 'Nu am putut adauga pachetul in cos.');
    } finally {
      this.addButton.textContent = originalText;
      this.render();
    }
  }
}

window.AnnetteBundleBuilder = window.AnnetteBundleBuilder || {
  addForProduct(productId) {
    const builder = document.querySelector(`bundle-builder[data-product-id="${productId}"]`);
    if (!builder) return false;
    builder.addToCart();
    return true;
  }
};

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
